export const PAGE_SIZE = 20;

export const isMonitoredLifecycle = (lifecycleStatus) =>
  lifecycleStatus === 'activated' || lifecycleStatus === 'activated-legacy';

export const lifecycleConfig = {
  activated: {label: 'Activated', color: 'var(--lifecycle-activated)'},
  'activated-legacy': {label: 'Activated - Legacy', color: 'var(--lifecycle-activated-legacy)'},
  shipped: {label: 'Shipped', color: 'var(--lifecycle-shipped)'},
  disabled: {label: 'Disabled', color: 'var(--lifecycle-disabled)'},
};

export const sortOptions = {
  name_asc: 'Name: A-Z',
  name_desc: 'Name: Z-A',
  update_desc: 'Last Update: Newest',
  update_asc: 'Last Update: Oldest',
  lifecycle_asc: 'Lifecycle: A-Z',
  lifecycle_desc: 'Lifecycle: Z-A',
};

// --- Edge status helpers -------------------------------------------------
// Status is derived from the per-check entries in `edge.currentStates`.
// deadmanCheck: 401 = offline, 202 = online. unhealthyCheck: 403 = unhealthy, 200 = online.

export const isEdgeOffline = (edge) => edge.currentStates?.deadmanCheck?.type === 401;

export const isEdgeUnhealthy = (edge) => edge.currentStates?.unhealthyCheck?.type === 403;

export const isEdgeHealthy = (edge) =>
  !!edge.currentStates && !isEdgeOffline(edge) && !isEdgeUnhealthy(edge);

const toMillis = (timestamp) => {
  if (!timestamp) return 0;
  if (typeof timestamp.toDate === 'function') return timestamp.toDate().getTime();
  if (typeof timestamp.seconds === 'number') return timestamp.seconds * 1000;
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

// Returns the most recent notification across all checks in `currentStates`.
export const getLatestNotification = (currentStates) => {
  if (!currentStates) return null;
  const entries = Object.values(currentStates).filter(Boolean);
  if (entries.length === 0) return null;
  return entries.reduce(
    (latest, entry) => (toMillis(entry.timestamp) > toMillis(latest.timestamp) ? entry : latest),
    entries[0]
  );
};

// Severity of a status code: 5xx > 4xx > 3xx > 2xx/1xx.
const getStatusSeverity = (type) => {
  if (!type) return 0;
  if (type >= 500) return 3;
  if (type >= 400) return 2;
  if (type >= 300) return 1;
  return 0;
};

// Returns the notification to display on the edge card.
// Priority: most severe active problem state first; if all checks are resolved,
// falls back to the latest notification by timestamp.
export const getDisplayNotification = (currentStates) => {
  if (!currentStates) return null;
  const entries = Object.values(currentStates).filter(Boolean);
  if (entries.length === 0) return null;

  const maxSeverity = Math.max(...entries.map((e) => getStatusSeverity(e.type)));

  if (maxSeverity > 0) {
    // Among problem-state entries at the highest severity, pick the most recent.
    const worstEntries = entries.filter((e) => getStatusSeverity(e.type) === maxSeverity);
    return worstEntries.reduce(
      (latest, entry) => (toMillis(entry.timestamp) > toMillis(latest.timestamp) ? entry : latest),
      worstEntries[0]
    );
  }

  return getLatestNotification(currentStates);
};
